import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isPromotableDraft, writebackApi, type WritebackPromotionResult } from '../../api/writeback';
import { useIsMirror } from '../../hooks/useIsMirror';

/**
 * ADR-010 — "Registruoti kliento knygoje": promotes an unposted a5suite draft
 * (id >= 1_000_000_000) into the client's real Apskaita5 book. Mirror
 * workspaces are read-only (ADR-004) EXCEPT this one sanctioned command.
 *
 * One click per decision: no confirmation dialog. The inline control reflects
 * the promotion state machine; outcome copy comes from the API verbatim — the
 * frontend never authors error text for API failures. After Confirmed/Reconciled
 * the chip replaces the button for good (the status query is the persistent
 * source of truth, so it survives reloads).
 */
export default function PromoteToClientBook({ entry }: { entry: { id: number; hasBeenPosted?: boolean } }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMirror = useIsMirror();

  const queryKey = ['journal-promotion', entry.id];
  const { data: promotion } = useQuery({
    queryKey,
    queryFn: () => writebackApi.getPromotion(entry.id),
    enabled: isMirror && isPromotableDraft(entry),
  });

  const promote = useMutation({
    mutationFn: () => writebackApi.promote(entry.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
  const check = useMutation({
    mutationFn: () => writebackApi.checkOutcome(entry.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (!isMirror || !isPromotableDraft(entry)) return null;

  // Latest action result wins (immediate feedback); the status query covers reloads.
  const result: WritebackPromotionResult | undefined = check.data ?? promote.data ?? promotion ?? undefined;
  const isSuccess = result?.state === 'Confirmed' || result?.state === 'Reconciled';
  const actionInFlight = promote.isPending || check.isPending;
  // Unknown, or a stale server Pending (send died mid-flight) with no action
  // running locally — both resolve through the ledger probe ("Patikrinti").
  const needsCheck =
    result?.state === 'Unknown' ||
    (!actionInFlight && (promotion?.state === 'Unknown' || promotion?.state === 'Pending'));

  if (isSuccess) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        {result!.legacyEntryId
          ? t('journal.promotionDoneNumber', { id: result!.legacyEntryId })
          : t('journal.promotionDone')}
      </span>
    );
  }

  const isUnknown = result?.state === 'Unknown';

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
        disabled={actionInFlight}
        onClick={() => (needsCheck ? check.mutate() : promote.mutate())}
      >
        {actionInFlight
          ? t('journal.promotionSending')
          : needsCheck
            ? t('journal.promotionCheck')
            : result?.state === 'Failed'
              ? t('journal.promotionRetry')
              : t('journal.promoteToClientBook')}
      </button>
      {result && result.message && (
        <div className={`text-xs max-w-xs text-right ${isUnknown ? 'text-amber-600' : 'text-red-600'}`}>
          {result.message}
        </div>
      )}
    </div>
  );
}
